import React, { useRef, useState, useEffect } from "react";
import { FaPaperPlane, FaXmark } from "react-icons/fa6";
import { MessageBox, Input, Button, SystemMessage } from "react-chat-elements";
import { checkHealth, useConversation } from "../../../services/leonardo";
import leonardoStore from "../../../stores/leonardoStore";
import remarkGfm from 'remark-gfm'
import ReactMarkdown from "react-markdown";
import ReactLoading from "react-loading";
import { format, register } from 'timeago.js';
import it from 'timeago.js/lib/lang/it';
import { IoIosChatbubbles } from "react-icons/io";

import PaymentFormPopup from "./PaymentForm/PaymentForm";
import PrivacyFormPopup from "./PrivacyForm/PrivacyForm";
import ChatButton from "./ChatButton/ChatButton";
import InsuranceFieldMessage from "./InsuranceFieldMessage/InsuranceFieldMessage";

import "react-chat-elements/dist/main.css";
import "./LeonardoContainer.css";

register('it', it);

const ChatHeader = ({ onClose, isLeonardoOnline }) => {
  const onlineStatus = isLeonardoOnline ? "Online" : "Offline"
  const onlineEmoji = isLeonardoOnline ? "🟢" : "🔴";

  return (
  <div className="chat-header">
    <img className="chat-logo" src="/bot-image.png" alt="Logo"/>
    <div className="chat-header-title">
      <span className="chat-title">Leonardo</span>
      <span className="chat-header-online-status">
        {onlineEmoji} 
        <span className={`chat-header-online ${onlineStatus.toLowerCase()}`}>{onlineStatus}</span>
      </span>
    </div>
    <Button 
      className="chat-close-btn close-btn" 
      color=""
      backgroundColor=""
      onClick={onClose}
      icon={{
        size: 18,
        component: <FaXmark />,
        float: 'left',
      }}>
    </Button>
  </div>
  );
};

const ChatWarning = () => {
  const [showWarning, setShowWarning] = useState(true);

  if (!showWarning) { return null };

  return (
    <div className="chat-warning-container">
      <SystemMessage
        className="chat-warning"
        text="Stai chattando con un'intelligenza artificiale. L'AI potrebbe commettere errori. Verifica sempre le informazioni."
      />
      <Button
        backgroundColor=""
        color=""
        className="chat-warning-close close-btn"
        onClick={() => setShowWarning(false)}
        icon={{
          size: 10,
          component: <FaXmark />,
          float: "left",
        }}
      />
    </div>
  );
};

const ChatBody = ({ messages, setPrivacyVisible, setPaymentVisible }) => {
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) { return };
  
    const observer = new MutationObserver(() => {
      container.scrollTop = container.scrollHeight;
    });
  
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  
    return () => observer.disconnect();
  }, []);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 60000);
  
    return () => clearInterval(interval);
  }, []);

  let groupedChatButtons = [];

  return (
    <div className="chat-body">
      <ChatWarning />
      <div className="chat-message-container" ref={messageContainerRef}>
        {messages.map((msg, index) => {
          
          if (msg.type === "loading") {
            return (
              <ReactLoading
                key={index}
                className="chat-loading"
                color=""
                height={30}
                width={30}
                type="bubbles"
              />
            );
          }

          if (msg.type === "chat-button") {
            groupedChatButtons.push(
              <ChatButton
                text={msg.text}
                key={index + "-chat-button"}
                onClick={() => {
                  msg.onClick();
                }}
              />
            );
            return null;
          }

          if (msg.type === "custom-privacy") {
            return (
              <MessageBox
                position="left"
                type="text"
                text={
                  <p>
                    Puoi visualizzarle
                    <span
                      onClick={() => setPrivacyVisible(true)}
                      style={{
                        color: 'var(--chatbot-primary-color)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        marginLeft: '4px'
                      }}
                    >
                     cliccando qui
                    </span>
                    .
                  </p>
                }
                date={msg.timestamp}
                 dateString={format(msg.timestamp, 'it')}
                key={ index + "-custom-privacy" }
              />
            );
          }

          if (msg.type === "custom-payment") {
            return (
              <MessageBox
                position="left"
                type="text"
                text={
                  <p>
                    Puoi procedere al pagamento
                    <span
                      onClick={leonardoStore.paymentEnabled? () => setPaymentVisible(true) : null}
                      style={{
                        color: 'var(--chatbot-primary-color)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        marginLeft: '4px'
                      }}
                    >
                     cliccando qui
                    </span>
                    .
                  </p>
                }
                date={msg.timestamp}
                dateString={format(msg.timestamp, 'it')}
                key={ index + "-custom-payment" }
              />
            );
          }

          if (msg.type === "insurance-field") {
            return (
              <InsuranceFieldMessage
                key={index + "-insurance-field"}
                date={msg.timestamp}
                dateString={format(msg.timestamp, 'it')}
              />
            );
          }


          return (
            <MessageBox
              key={`${index}-${tick}`}
              position={msg.position}
              type={msg.type}
              date={msg.timestamp}
               dateString={format(msg.timestamp, 'it')}
              text={
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({node, ...props}) => <p style={{whiteSpace: 'pre-wrap'}} {...props} />
                  }}
                >{msg.text}</ReactMarkdown>}
            />
          );
        })}

        {groupedChatButtons.length > 0 && (
          <div className="chat-button-group">
            {groupedChatButtons}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

const LeonardoContainer = () => {
  const [isChatAvailable, setIsChatAvailable] = useState(false);
  const [isLeonardoOnline, setIsLeonardoOnline] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { messages, sendGreetings, sendMessage, isChatting, setMessages } = useConversation();

  const [privacyVisible, setPrivacyVisible] = useState(false);
  const [paymentVisible, setPaymentVisible] = useState(false);

  const handlePaymentResult = (success) => {
  setMessages(prev => [
    ...prev,
    {
      position: "left",
      type: "text",
      text: success
        ? "Il pagamento è andato a buon fine! Grazie per aver scelto il nostro servizio."
        : "Il pagamento non è andato a buon fine. Ti preghiamo di riprovare più tardi.",
      timestamp: Date.now(),
    }
  ]);
  leonardoStore.paymentEnabled = false;
  setPaymentVisible(false); 
};


  useEffect(() => {
  
    sendGreetings()
      .then((response) => {
        if (response.success) {
          setIsChatAvailable(true);
          setIsOpen(true);
        } else {
          setIsChatAvailable(false);
          setIsOpen(false);
        }
    });
  }
  , []);

  useEffect(() => {
  const updateStatus = async () => {
    try {
      const online = await checkHealth();
      setIsLeonardoOnline(online);
    } catch {
      setIsLeonardoOnline(false);
    }
  };

  updateStatus();
  const interval = setInterval(updateStatus, 60000);
  return () => clearInterval(interval);
}, []);

  useEffect(() => {
    if (isChatAvailable) {
      document.body.classList.add('hide-hs-chat');
      try {
        const hide = window.HubSpotConversations && window.HubSpotConversations.widget && window.HubSpotConversations.widget.hide;
        if (typeof hide === "function") { hide() };
      } catch {}
    } else {
      document.body.classList.remove('hide-hs-chat');
      try {
        const show = window.HubSpotConversations && window.HubSpotConversations.widget && window.HubSpotConversations.widget.show;
        if (typeof show === "function") { show() };
      } catch {}
    }
    return () => {
      document.body.classList.remove('hide-hs-chat');
      try {
        const show = window.HubSpotConversations && window.HubSpotConversations.widget && window.HubSpotConversations.widget.show;
        if (typeof show === "function") { show() };
      } catch {}
    };
  }, [isChatAvailable]);

  return (
    <div className="leonardo-chat-container">
      <PrivacyFormPopup 
        show={privacyVisible} 
        onClose={
          () => {
            setPrivacyVisible(false)
          }
        }
        onAccept={
          () => {
            setPrivacyVisible(false)
            leonardoStore.userHasSeenPrivacyForm = true;
            leonardoStore.privacyEnabled = false;
          }
        }
      />
      <PaymentFormPopup
        show={paymentVisible}
        onClose={() => {
          setPaymentVisible(false)
        }}
        onPaymentResult={handlePaymentResult}
      />

      <button hidden={!isChatAvailable || isOpen} className="chat-toggle-btn" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? "Close Chat" : <IoIosChatbubbles />}
      </button>

      <div className={`chat-box ${isOpen && isChatAvailable? "show" : "hide"}`}>
        <ChatHeader onClose={() => setIsOpen(false)} isLeonardoOnline={isLeonardoOnline} />
        <ChatBody className="chat-body" messages={messages} setPrivacyVisible={setPrivacyVisible} setPaymentVisible={setPaymentVisible}  />
        <Input
          className="chat-input"
          placeholder="Scrivi il tuo messaggio..."
          onKeyPress={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              if (isChatting || !isLeonardoOnline) { return }; 
              sendMessage(e.target.value.trim());
              e.target.value = "";
            }
          }}
          rightButtons={
            <Button
              className="chat-send-btn"
              color=""
              backgroundColor=""
              disabled={isChatting || !isLeonardoOnline}
              onClick={() => {
                const input = document.querySelector(".chat-input input");
                if (input && input.value.trim()) {
                  sendMessage(input.value.trim());
                  input.value = "";
                }
              }}
              icon={{
                size: 18,
                component: <FaPaperPlane />,
                float: 'left',
              }}
            />
          }
        />
      </div>
    </div>
  );
};


export default LeonardoContainer;
